import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { A11yModule } from '@angular/cdk/a11y';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FileDropzoneComponent } from '../../../components/file-dropzone/file-dropzone.component';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { TestRunService } from '../../../services/test-run.service';
import { RobotImportResult, RobotImportSkippedTest } from '../../../models/robot-import.model';

const ACCEPTED_EXTENSION = 'xml';
const MAX_FILE_SIZE = 6 * 1024 * 1024;

@Component({
  selector: 'app-robot-upload-modal',
  imports: [A11yModule, TranslatePipe, MatIcon, MatProgressSpinner, FileDropzoneComponent, SectionTitleComponent],
  templateUrl: './robot-upload-modal.component.html',
  styleUrl: './robot-upload-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RobotUploadModalComponent {
  private readonly testRunService = inject(TestRunService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly visible = model(false);
  readonly imported = output<RobotImportResult>();

  readonly acceptedFileTypes = '.xml';

  readonly file = signal<File | null>(null);
  readonly busy = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly result = signal<RobotImportResult | null>(null);

  private triggerElement: HTMLElement | null = null;

  constructor() {
    let wasVisible = false;
    effect(() => {
      const open = this.visible();
      if (open && !wasVisible) {
        this.triggerElement = document.activeElement as HTMLElement | null;
        document.body.style.overflow = 'hidden';
      } else if (!open && wasVisible) {
        document.body.style.overflow = '';
        this.triggerElement?.focus();
        this.triggerElement = null;
      }
      wasVisible = open;
    });
    this.destroyRef.onDestroy(() => (document.body.style.overflow = ''));
  }

  onFileSelected(file: File): void {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (extension !== ACCEPTED_EXTENSION) {
      this.file.set(null);
      this.errorMessage.set(this.translate.instant('testResults.upload.errors.fileType'));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      this.file.set(null);
      this.errorMessage.set(this.translate.instant('testResults.upload.errors.fileTooLarge'));
      return;
    }
    this.errorMessage.set(null);
    this.file.set(file);
  }

  skipReason(skipped: RobotImportSkippedTest): string | null {
    if (skipped.code) {
      const key = `testResults.upload.skipReasons.${skipped.code}`;
      const text = this.translate.instant(key, skipped.params ?? {});
      if (text !== key && !text.includes('{{')) return text;
    }
    return skipped.reason || null;
  }

  onFileCleared(): void {
    this.file.set(null);
  }

  upload(): void {
    const file = this.file();
    if (!file || this.busy()) return;
    this.busy.set(true);
    this.errorMessage.set(null);
    this.testRunService
      .uploadRobotOutput(this.projectId(), file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.result.set(result);
          this.busy.set(false);
          this.imported.emit(result);
        },
        error: (err: HttpErrorResponse) => {
          this.errorMessage.set(this.messageFor(err));
          this.busy.set(false);
        },
      });
  }

  close(): void {
    this.visible.set(false);
    this.reset();
  }

  // Der Server liefert bei 400 eine lesbare Meldung ({ error: ... }); nur wenn keine
  // vorliegt (z. B. Netzfehler), auf eine generische Übersetzung zurückfallen.
  private messageFor(err: HttpErrorResponse): string {
    const serverMessage = err?.error?.error;
    return typeof serverMessage === 'string' && serverMessage.length > 0
      ? serverMessage
      : this.translate.instant('testResults.upload.errors.generic');
  }

  private reset(): void {
    this.file.set(null);
    this.busy.set(false);
    this.errorMessage.set(null);
    this.result.set(null);
  }
}
